import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Grid,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';

const RegistrationRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seriesId, setSeriesId] = useState(null);
  const [seriesInfo, setSeriesInfo] = useState(null);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'approved-list'
  const [approvedListFilter, setApprovedListFilter] = useState('all'); // 'all', 'main', 'waiting'
  const [approvedRoleFilter, setApprovedRoleFilter] = useState('all');
  
  // Dialog states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [useExistingPic, setUseExistingPic] = useState('0');
  const [rejectionReason, setRejectionReason] = useState('');
  const [bulkMode, setBulkMode] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [rejectionLoading, setRejectionLoading] = useState(false);
  
  // Image preview states
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');

  const urlParams = new URLSearchParams(window.location.search);
  const inviteCode = urlParams.get('i');
  const seriesKeyFromUrl = urlParams.get('s');

  useEffect(() => {
    if (seriesKeyFromUrl) {
      // If series key is directly provided (from MI page)
      setSeriesId(seriesKeyFromUrl);
    } else if (inviteCode) {
      // If invite code is provided (from auction page)
      fetchSeriesId();
    }
  }, [inviteCode, seriesKeyFromUrl]);

  useEffect(() => {
    if (seriesId) {
      loadRequests();
    }
  }, [seriesId]);

  const fetchSeriesId = async () => {
    try {
      const response = await fetch(
        global.config.app.backend_api_url + 'auction/get_current_auction_status_by_invite_code/',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            invite_code: inviteCode,
          }),
        }
      );

      const data = await response.json();
      if (data.auction_details && data.auction_details.main[0]) {
        setSeriesId(data.auction_details.main[0].series_id);
      }
    } catch (error) {
      console.error('Error fetching series ID:', error);
      toast.error('Failed to load series information');
    }
  };

  const loadRequests = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        global.config.app.backend_api_url + 'public_reg_admin/get_registration_requests',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            series_id: seriesId,
          }),
        }
      );

      const data = await response.json();
      console.log('API Response:', data); // Debug log
      if (data.status === 1) {
        setRequests(data.requests || []);
        if (data.series_info) {
          setSeriesInfo(data.series_info);
        }
      } else {
        toast.error(data.message || 'Failed to load requests');
      }
    } catch (error) {
      console.error('Error loading requests:', error);
      toast.error('Failed to load registration requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClick = (request) => {
    setCurrentRequest(request);
    // Default to new pic always
    setUseExistingPic('0');
    setApproveDialogOpen(true);
    setBulkMode(false);
  };

  const handleRejectClick = (request) => {
    setCurrentRequest(request);
    setRejectionReason('');
    setRejectDialogOpen(true);
    setBulkMode(false);
  };

  const handleBulkApprove = () => {
    if (selectedRequests.length === 0) {
      toast.warning('Please select at least one request');
      return;
    }
    setUseExistingPic('0');
    setApproveDialogOpen(true);
    setBulkMode(true);
  };

  const handleBulkReject = () => {
    if (selectedRequests.length === 0) {
      toast.warning('Please select at least one request');
      return;
    }
    setRejectionReason('');
    setRejectDialogOpen(true);
    setBulkMode(true);
  };

  const confirmApprove = async () => {
    setApprovalLoading(true);
    try {
      if (bulkMode) {
        const response = await fetch(
          global.config.app.backend_api_url + 'public_reg_admin/bulk_approve',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              request_ids: selectedRequests,
              use_existing_profile_pic: useExistingPic,
            }),
          }
        );

        const data = await response.json();
        if (data.status === 1) {
          toast.success(data.message || 'Requests approved successfully');
          setSelectedRequests([]);
          loadRequests();
        } else {
          toast.error(data.message || 'Failed to approve requests');
        }
      } else {
        const response = await fetch(
          global.config.app.backend_api_url + 'public_reg_admin/approve_request',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              req_series_participant_id: currentRequest.req_series_participant_id,
              use_existing_profile_pic: useExistingPic,
            }),
          }
        );

        const data = await response.json();
        if (data.status === 1) {
          toast.success(data.message || 'Request approved successfully');
          loadRequests();
        } else {
          toast.error(data.message || 'Failed to approve request');
        }
      }
    } catch (error) {
      console.error('Error approving:', error);
      toast.error('Failed to approve');
    } finally {
      setApprovalLoading(false);
      setApproveDialogOpen(false);
      setCurrentRequest(null);
    }
  };

  const confirmReject = async () => {
    setRejectionLoading(true);
    try {
      if (bulkMode) {
        const response = await fetch(
          global.config.app.backend_api_url + 'public_reg_admin/bulk_reject',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              request_ids: selectedRequests,
              rejection_reason: rejectionReason,
            }),
          }
        );

        const data = await response.json();
        if (data.status === 1) {
          toast.success(data.message || 'Requests rejected successfully');
          setSelectedRequests([]);
          loadRequests();
        } else {
          toast.error(data.message || 'Failed to reject requests');
        }
      } else {
        const response = await fetch(
          global.config.app.backend_api_url + 'public_reg_admin/reject_request',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              req_series_participant_id: currentRequest.req_series_participant_id,
              rejection_reason: rejectionReason,
            }),
          }
        );

        const data = await response.json();
        if (data.status === 1) {
          toast.success(data.message || 'Request rejected successfully');
          loadRequests();
        } else {
          toast.error(data.message || 'Failed to reject request');
        }
      }
    } catch (error) {
      console.error('Error rejecting:', error);
      toast.error('Failed to reject');
    } finally {
      setRejectionLoading(false);
      setRejectDialogOpen(false);
      setCurrentRequest(null);
      setRejectionReason('');
    }
  };

  const toggleSelectRequest = (reqId) => {
    setSelectedRequests((prev) =>
      prev.includes(reqId) ? prev.filter((id) => id !== reqId) : [...prev, reqId]
    );
  };

  const handleImagePreview = (imageUrl, title) => {
    setPreviewImage(imageUrl);
    setPreviewTitle(title);
    setImagePreviewOpen(true);
  };

  const handleSelectAll = () => {
    const pendingIds = filteredRequests
      .filter(req => req.request_status === 'pending')
      .map(req => req.req_series_participant_id);
    setSelectedRequests(pendingIds);
  };

  const handleDeselectAll = () => {
    setSelectedRequests([]);
  };

  const isAllSelected = () => {
    const pendingRequests = filteredRequests.filter(req => req.request_status === 'pending');
    return pendingRequests.length > 0 && selectedRequests.length === pendingRequests.length;
  };

  const handleCopyLink = (link, linkType) => {
    if (link) {
      navigator.clipboard.writeText(link)
        .then(() => {
          toast.success(`${linkType} copied to clipboard!`);
        })
        .catch((err) => {
          console.error('Failed to copy:', err);
          toast.error('Failed to copy link');
        });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return { bg: '#FFF3E0', color: '#F57C00' };
      case 'approved':
        return { bg: '#E8F5E9', color: '#2E7D32' };
      case 'rejected':
        return { bg: '#FFEBEE', color: '#C62828' };
      default:
        return { bg: '#E0E0E0', color: '#616161' };
    }
  };

  // Get approved participants with sl_no
  const getApprovedParticipants = () => {
    return requests
      .filter(req => req.request_status === 'approved' && req.sl_no !== null && req.sl_no !== undefined)
      .sort((a, b) => parseInt(a.sl_no || 0) - parseInt(b.sl_no || 0));
  };

  // Filter approved participants based on filters
  const getFilteredApprovedParticipants = () => {
    let filtered = getApprovedParticipants();

    // Filter by list type
    if (approvedListFilter === 'main') {
      filtered = filtered.filter(req => req.in_waiting_list == 0);
    } else if (approvedListFilter === 'waiting') {
      filtered = filtered.filter(req => req.in_waiting_list == 1);
    }

    // Filter by role
    if (approvedRoleFilter !== 'all') {
      filtered = filtered.filter(req => req.role === approvedRoleFilter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(req =>
        req.req_player_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.req_mobile_no?.includes(searchTerm)
      );
    }

    return filtered;
  };

  // Get statistics for approved list
  const getApprovedStats = () => {
    const approved = getApprovedParticipants();
    const mainList = approved.filter(r => r.in_waiting_list == 0);
    const waitingList = approved.filter(r => r.in_waiting_list == 1);

    const roles = {
      'Batter': approved.filter(r => r.role === 'Batter').length,
      'Bowler': approved.filter(r => r.role === 'Bowler').length,
      'All Rounder': approved.filter(r => r.role === 'All Rounder').length,
      'Wicket Keeper': approved.filter(r => r.role === 'Wicket Keeper').length,
    };

    return {
      total: approved.length,
      mainListCount: mainList.length,
      waitingListCount: waitingList.length,
      byRole: roles
    };
  };

  const filteredRequests = requests.filter((req) => {
    // Filter by status
    const statusMatch = filterStatus === 'all' || req.request_status === filterStatus;
    
    // Filter by search term (name or mobile number)
    const searchMatch = searchTerm === '' || 
      req.req_player_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.req_mobile_no?.includes(searchTerm);
    
    return statusMatch && searchMatch;
  });

  const pendingCount = requests.filter((r) => r.request_status === 'pending').length;
  const approvedCount = requests.filter((r) => r.request_status === 'approved').length;
  const rejectedCount = requests.filter((r) => r.request_status === 'rejected').length;

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="container-fluid py-4 bg-light min-vh-100">
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Registration Requests
            </Typography>
            {seriesInfo?.series_name && (
              <Typography variant="h6" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {seriesInfo.series_name}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Links Section */}
        {(seriesInfo?.public_registration_link || seriesInfo?.invite_link) && (
          <Card sx={{ mb: 2, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ py: 2 }}>
              {/* Public Registration Link */}
              {seriesInfo?.public_registration_link && (
                <Box sx={{ mb: seriesInfo?.invite_link ? 2 : 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LinkIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Public Registration Link:
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' }
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        flex: 1,
                        bgcolor: 'white',
                        p: 1.5,
                        borderRadius: 1,
                        border: '1px solid #d0d0d0',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        wordBreak: 'break-all',
                        color: 'primary.main'
                      }}
                    >
                      {seriesInfo.public_registration_link}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => handleCopyLink(seriesInfo.public_registration_link, 'Public registration link')}
                      sx={{ 
                        minWidth: '120px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Copy Link
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => window.open(seriesInfo.public_registration_link, '_blank')}
                      sx={{ 
                        minWidth: '120px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Open Link
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Invite Link */}
              {seriesInfo?.invite_link && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LinkIcon sx={{ color: 'secondary.main' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Invite Link:
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' }
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        flex: 1,
                        bgcolor: 'white',
                        p: 1.5,
                        borderRadius: 1,
                        border: '1px solid #d0d0d0',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        wordBreak: 'break-all',
                        color: 'secondary.main'
                      }}
                    >
                      {seriesInfo.invite_link}
                    </Typography>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => handleCopyLink(seriesInfo.invite_link, 'Invite link')}
                      sx={{ 
                        minWidth: '120px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Copy Link
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => window.open(seriesInfo.invite_link, '_blank')}
                      sx={{ 
                        minWidth: '120px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Open Link
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Comparison Report Link */}
        {seriesId && (
          <Card sx={{ mb: 2, bgcolor: '#e3f2fd', border: '1px solid #2196F3' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <AssessmentIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Comparison Report
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Compare series participants with registration requests
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AssessmentIcon />}
                  onClick={() => navigate(`/comparison-report?s=${seriesId}`)}
                  sx={{ 
                    minWidth: '180px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  View Comparison Report
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* View Mode Switcher */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => {
              if (newMode !== null) {
                setViewMode(newMode);
                setSelectedRequests([]);
              }
            }}
            size="small"
          >
            <ToggleButton value="cards">
              <ViewModuleIcon sx={{ mr: 1 }} />
              Card View
            </ToggleButton>
            <ToggleButton value="approved-list">
              <ViewListIcon sx={{ mr: 1 }} />
              Approved List
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Filter Tabs - Only show in card view */}
        {viewMode === 'cards' && (
          <Tabs
            value={filterStatus}
            onChange={(e, newValue) => {
              setFilterStatus(newValue);
              setSelectedRequests([]);
            }}
            sx={{ mb: 2 }}
          >
            <Tab label="All" value="all" />
            <Tab
              label={
                <Badge badgeContent={pendingCount} color="warning" max={9999}>
                  Pending
                </Badge>
              }
              value="pending"
            />
            <Tab
              label={
                <Badge badgeContent={approvedCount} color="success" max={9999}>
                  Approved
                </Badge>
              }
              value="approved"
            />
            <Tab
              label={
                <Badge badgeContent={rejectedCount} color="error" max={9999}>
                  Rejected
                </Badge>
              }
              value="rejected"
            />
          </Tabs>
        )}

        {/* Search Box - Only in card view */}
        {viewMode === 'cards' && (
          <Box sx={{ mb: 2, mt: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by player name or mobile number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                ),
                endAdornment: searchTerm && (
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    sx={{ mr: -1 }}
                  >
                    <ClearIcon />
                  </IconButton>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
            {searchTerm && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Found {filteredRequests.length} result(s)
              </Typography>
            )}
          </Box>
        )}

        {/* Select All Option - Only in card view */}
        {viewMode === 'cards' && filteredRequests.filter(req => req.request_status === 'pending').length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAllSelected()}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleSelectAll();
                    } else {
                      handleDeselectAll();
                    }
                  }}
                  indeterminate={selectedRequests.length > 0 && !isAllSelected()}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {isAllSelected() ? 'Deselect All' : 'Select All'} 
                  {` (${filteredRequests.filter(req => req.request_status === 'pending').length} pending)`}
                </Typography>
              }
            />
          </Box>
        )}

        {/* Bulk Actions - Only in card view */}
        {viewMode === 'cards' && selectedRequests.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={handleBulkApprove}
            >
              Approve Selected ({selectedRequests.length})
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleBulkReject}
            >
              Reject Selected ({selectedRequests.length})
            </Button>
            <Button variant="outlined" onClick={() => setSelectedRequests([])}>
              Clear Selection
            </Button>
          </Box>
        )}
      </Box>

      {/* Card View - Requests List */}
      {viewMode === 'cards' && (
      <div className="row">
        {filteredRequests.length === 0 ? (
          <div className="col-12">
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 5 }}>
                <Typography variant="h6" color="textSecondary">
                  No {filterStatus !== 'all' ? filterStatus : ''} requests found
                </Typography>
              </CardContent>
            </Card>
          </div>
        ) : (
          filteredRequests.map((request) => {
            const isPending = request.request_status === 'pending';
            const statusColors = getStatusColor(request.request_status);

            return (
              <div key={request.req_series_participant_id} className="col-12 col-md-6 col-lg-4 mb-3">
                <Card sx={{ height: '100%', position: 'relative' }}>
                  {/* Selection Checkbox */}
                  {isPending && (
                    <Checkbox
                      checked={selectedRequests.includes(request.req_series_participant_id)}
                      onChange={() => toggleSelectRequest(request.req_series_participant_id)}
                      sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}
                    />
                  )}

                  <CardContent>
                    {/* Status Badge and Request Number */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip
                        label={request.request_status.toUpperCase()}
                        size="small"
                        sx={{
                          backgroundColor: statusColors.bg,
                          color: statusColors.color,
                          fontWeight: 600,
                        }}
                      />
                      {request.internal_req_sl_no && (
                        <Chip
                          label={`REQ #${request.internal_req_sl_no}`}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: '#2196F3',
                            color: '#2196F3',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        />
                      )}
                    </Box>

                    {/* Player Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        src={request.req_profile_pic || '/placeholder.svg'}
                        alt={request.req_player_name}
                        sx={{ 
                          width: 64, 
                          height: 64, 
                          mr: 2,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.8,
                            transform: 'scale(1.05)',
                            transition: 'all 0.2s'
                          }
                        }}
                        onClick={() => handleImagePreview(request.req_profile_pic, `${request.req_player_name} - Profile Picture`)}
                      />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {request.req_player_name}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          component="a"
                          href={`https://wa.me/${request.req_mobile_no.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: '#25D366',
                            textDecoration: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontWeight: 500,
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          📱 {request.req_mobile_no}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Details */}
                    <Box sx={{ mb: 2 }}>
                      {request.role && (
                        <Typography variant="body2" color="textSecondary">
                          <strong>Role:</strong> {request.role}
                        </Typography>
                      )}
                      <Typography variant="body2" color="textSecondary">
                        <strong>Batting:</strong> {request.batting_style || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Bowling:</strong> {request.bowling_style || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Keeping:</strong> {request.keeping || 'N/A'}
                      </Typography>
                      {request.jersey_no && (
                        <Typography variant="body2" color="textSecondary">
                          <strong>Jersey:</strong> #{request.jersey_no}
                          {request.jersey_name && ` - ${request.jersey_name}`}
                          {request.jersey_size && ` (${request.jersey_size})`}
                        </Typography>
                      )}
                      {request.jersey_lower_size && (
                        <Typography variant="body2" color="textSecondary">
                          <strong>Pant size:</strong> {request.jersey_lower_size}
                        </Typography>
                      )}
                      {request.district_name && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          <strong>District:</strong> {request.district_name}
                        </Typography>
                      )}
                      {request.previous_team_details && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          <strong>Current Club:</strong> {request.previous_team_details}
                        </Typography>
                      )}
                      {request.comments && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          <strong>Comments:</strong> {request.comments}
                        </Typography>
                      )}
                      {request.designation && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          <strong>Designation:</strong> {request.designation}
                        </Typography>
                      )}
                      {request.office_location && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          <strong>Office Location:</strong> {request.office_location}
                        </Typography>
                      )}
                      {request.job_type && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          <strong>Job Type:</strong> {request.job_type}
                        </Typography>
                      )}
                      {request.foreign_player_details && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          <strong>Foreign Player (RO):</strong> {request.foreign_player_details}
                        </Typography>
                      )}
                    </Box>

                    {/* Office ID Photo */}
                    {request.office_id_photo && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
                          Office ID Photo:
                        </Typography>
                        <Box 
                          component="img"
                          src={request.office_id_photo}
                          alt="Office ID Photo"
                          sx={{
                            width: '100%',
                            maxWidth: 200,
                            height: 'auto',
                            borderRadius: 1,
                            border: '2px solid #1976d2',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8,
                              transform: 'scale(1.02)',
                              transition: 'all 0.2s',
                              boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                            }
                          }}
                          onClick={() => handleImagePreview(request.office_id_photo, `${request.req_player_name} - Office ID Photo`)}
                        />
                      </Box>
                    )}

                    {/* Profile Pictures (if existing user) */}
                    {request.existing_profile_pic && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Profile Pictures:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Avatar
                              src={request.existing_profile_pic}
                              sx={{ 
                                width: 50, 
                                height: 50, 
                                mb: 0.5,
                                cursor: 'pointer',
                                '&:hover': {
                                  opacity: 0.8,
                                  transform: 'scale(1.1)',
                                  transition: 'all 0.2s'
                                }
                              }}
                              onClick={() => handleImagePreview(request.existing_profile_pic, `${request.req_player_name} - Existing Profile`)}
                            />
                            <Typography variant="caption">Existing</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Avatar
                              src={request.req_profile_pic}
                              sx={{ 
                                width: 50, 
                                height: 50, 
                                mb: 0.5,
                                cursor: 'pointer',
                                '&:hover': {
                                  opacity: 0.8,
                                  transform: 'scale(1.1)',
                                  transition: 'all 0.2s'
                                }
                              }}
                              onClick={() => handleImagePreview(request.req_profile_pic, `${request.req_player_name} - New Profile`)}
                            />
                            <Typography variant="caption">New</Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* GPay Screenshot */}
                    {request.gpay_screenshot && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Payment Screenshot:
                        </Typography>
                        <Box 
                          component="img"
                          src={request.gpay_screenshot}
                          alt="Payment Screenshot"
                          sx={{
                            width: '100%',
                            maxWidth: 200,
                            height: 'auto',
                            borderRadius: 1,
                            border: '2px solid #e0e0e0',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8,
                              transform: 'scale(1.02)',
                              transition: 'all 0.2s'
                            }
                          }}
                          onClick={() => handleImagePreview(request.gpay_screenshot, `${request.req_player_name} - Payment Screenshot`)}
                        />
                      </Box>
                    )}

                    {/* Aadhar Card Images */}
                    {(request.aadhar_front_pic || request.aadhar_back_pic) && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#856404' }}>
                          Aadhar Card:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {request.aadhar_front_pic && (
                            <Box>
                              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600, color: '#856404' }}>
                                Front
                              </Typography>
                              <Box 
                                component="img"
                                src={request.aadhar_front_pic}
                                alt="Aadhar Front"
                                sx={{
                                  width: '100%',
                                  maxWidth: 200,
                                  height: 'auto',
                                  borderRadius: 1,
                                  border: '2px solid #ffc107',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    opacity: 0.8,
                                    transform: 'scale(1.02)',
                                    transition: 'all 0.2s',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                  }
                                }}
                                onClick={() => handleImagePreview(request.aadhar_front_pic, `${request.req_player_name} - Aadhar Front`)}
                              />
                            </Box>
                          )}
                          {request.aadhar_back_pic && (
                            <Box>
                              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600, color: '#856404' }}>
                                Back
                              </Typography>
                              <Box 
                                component="img"
                                src={request.aadhar_back_pic}
                                alt="Aadhar Back"
                                sx={{
                                  width: '100%',
                                  maxWidth: 200,
                                  height: 'auto',
                                  borderRadius: 1,
                                  border: '2px solid #ffc107',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    opacity: 0.8,
                                    transform: 'scale(1.02)',
                                    transition: 'all 0.2s',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                  }
                                }}
                                onClick={() => handleImagePreview(request.aadhar_back_pic, `${request.req_player_name} - Aadhar Back`)}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Actions */}
                    {isPending && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          fullWidth
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleApproveClick(request)}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          fullWidth
                          startIcon={<CancelIcon />}
                          onClick={() => handleRejectClick(request)}
                        >
                          Reject
                        </Button>
                      </Box>
                    )}

                    {/* Timestamp */}
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: 'block', mt: 2 }}
                    >
                      Submitted: {new Date(request.added_on).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </div>
            );
          })
        )}
      </div>
      )}

      {/* Approved List View */}
      {viewMode === 'approved-list' && (
        <Box>
          {/* Statistics Dashboard */}
          {(() => {
            const stats = getApprovedStats();
            return (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: '#E8F5E9' }}>
                    <CardContent>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                        {stats.total}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Approved
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: '#E3F2FD' }}>
                    <CardContent>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976D2' }}>
                        {stats.mainListCount}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Main List
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: '#FFF3E0' }}>
                    <CardContent>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#F57C00' }}>
                        {stats.waitingListCount}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Waiting List
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        By Role:
                      </Typography>
                      {Object.entries(stats.byRole).map(([role, count]) => (
                        <Typography key={role} variant="caption" display="block">
                          {role}: {count}
                        </Typography>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            );
          })()}

          {/* Filters */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600 }}>
                    List Type
                  </Typography>
                  <Select
                    value={approvedListFilter}
                    onChange={(e) => setApprovedListFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Participants</MenuItem>
                    <MenuItem value="main">Main List Only</MenuItem>
                    <MenuItem value="waiting">Waiting List Only</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600 }}>
                    Role
                  </Typography>
                  <Select
                    value={approvedRoleFilter}
                    onChange={(e) => setApprovedRoleFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Roles</MenuItem>
                    <MenuItem value="Batter">Batter</MenuItem>
                    <MenuItem value="Bowler">Bowler</MenuItem>
                    <MenuItem value="All Rounder">All Rounder</MenuItem>
                    <MenuItem value="Wicket Keeper">Wicket Keeper</MenuItem>
                  </Select>
                </FormControl>

                <Box sx={{ flex: 1, minWidth: 250 }}>
                  <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600, display: 'block' }}>
                    Search
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search by name or mobile..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <SearchIcon sx={{ color: 'action.active', mr: 1, fontSize: 20 }} />
                      ),
                      endAdornment: searchTerm && (
                        <IconButton
                          size="small"
                          onClick={() => setSearchTerm('')}
                          sx={{ p: 0.5 }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      ),
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Approved Participants Table */}
          {(() => {
            const filteredApproved = getFilteredApprovedParticipants();
            
            if (filteredApproved.length === 0) {
              return (
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 5 }}>
                    <Typography variant="h6" color="textSecondary">
                      No approved participants found
                    </Typography>
                  </CardContent>
                </Card>
              );
            }

            return (
              <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
                <Table>
                <TableBody>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell align="center" sx={{ fontWeight: 700, width: 80 }}>Sl No</TableCell>
                      <TableCell sx={{ fontWeight: 700,  width: 180 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700,  width: 80 }}>Mobile</TableCell>
                      <TableCell sx={{ fontWeight: 700,  width: 80 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 700,  width: 80 }}>Batting</TableCell>
                      <TableCell sx={{ fontWeight: 700,  width: 80 }}>Bowling</TableCell>
                      <TableCell sx={{ fontWeight: 700,  width: 80 }}>District</TableCell>
                      <TableCell sx={{ fontWeight: 700,  width: 100 }}>Designation</TableCell>
                      <TableCell sx={{ fontWeight: 700,  width: 100 }}>Office Location</TableCell>
                      <TableCell sx={{ fontWeight: 700,  width: 100 }}>Job Type</TableCell>
                      <TableCell sx={{ fontWeight: 700,  width: 80 }}>Jersey</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, width: 80 }}>Status</TableCell>
                    </TableRow>
                 
                    {filteredApproved.map((participant, index) => (
                      <TableRow
                        key={participant.req_series_participant_id}
                        sx={{
                          '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                          '&:hover': { bgcolor: '#f0f0f0' }
                        }}
                      >
                        <TableCell align="center" sx={{ fontWeight: 600 }}>{participant.sl_no}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              src={participant.req_profile_pic || '/placeholder.svg'}
                              alt={participant.req_player_name}
                              sx={{ width: 32, height: 32 }}
                            />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {participant.req_player_name}
                              </Typography>
                              {participant.internal_req_sl_no && (
                                <Chip
                                  label={`REQ #${participant.internal_req_sl_no}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderColor: '#2196F3',
                                    color: '#2196F3',
                                    fontWeight: 600,
                                    fontSize: '0.65rem',
                                    height: '18px',
                                    mt: 0.5,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography
                            component="a"
                            href={`https://wa.me/${participant.req_mobile_no.replace(/[^0-9]/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: '#25D366',
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            {participant.req_mobile_no}
                          </Typography>
                        </TableCell>
                        <TableCell>{participant.role || 'N/A'}</TableCell>
                        <TableCell>{participant.batting_style || 'N/A'}</TableCell>
                        <TableCell>{participant.bowling_style || 'N/A'}</TableCell>
                        <TableCell>{participant.district_name || '-'}</TableCell>
                        <TableCell>{participant.designation || '-'}</TableCell>
                        <TableCell>{participant.office_location || '-'}</TableCell>
                        <TableCell>{participant.job_type || '-'}</TableCell>
                        <TableCell>
                          #{participant.jersey_no}
                          {participant.jersey_name && ` - ${participant.jersey_name}`}
                          {participant.jersey_lower_size && (
                            <Typography component="span" variant="body2" color="textSecondary" display="block" sx={{ fontSize: '0.75rem', mt: 0.25 }}>
                              Pant: {participant.jersey_lower_size}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={participant.in_waiting_list == 1 ? 'Waiting' : 'Main'}
                            size="small"
                            color={participant.in_waiting_list == 1 ? 'warning' : 'success'}
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()}
        </Box>
      )}

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {bulkMode 
            ? `Approve ${selectedRequests.length} Requests` 
            : currentRequest?.internal_req_sl_no 
              ? `Approve Request #${currentRequest.internal_req_sl_no}` 
              : 'Approve Request'
          }
        </DialogTitle>
        <DialogContent>
          {!bulkMode && currentRequest && currentRequest.existing_profile_pic && (
            <FormControl component="fieldset">
              <FormLabel component="legend">Select Profile Picture:</FormLabel>
              <RadioGroup value={useExistingPic} onChange={(e) => setUseExistingPic(e.target.value)}>
                <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar
                      src={currentRequest.existing_profile_pic}
                      sx={{ width: 80, height: 80, mb: 1, mx: 'auto' }}
                    />
                    <FormControlLabel value="1" control={<Radio />} label="Use Existing" />
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar
                      src={currentRequest.req_profile_pic}
                      sx={{ width: 80, height: 80, mb: 1, mx: 'auto' }}
                    />
                    <FormControlLabel value="0" control={<Radio />} label="Use New" />
                  </Box>
                </Box>
              </RadioGroup>
            </FormControl>
          )}
          {bulkMode && (
            <Typography>
              Are you sure you want to approve {selectedRequests.length} request(s)? All will use new
              profile pictures.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)} disabled={approvalLoading}>
            Cancel
          </Button>
          <Button 
            onClick={confirmApprove} 
            variant="contained" 
            color="success"
            disabled={approvalLoading}
            startIcon={approvalLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {approvalLoading ? 'Processing...' : 'Confirm Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {bulkMode 
            ? `Reject ${selectedRequests.length} Requests` 
            : currentRequest?.internal_req_sl_no 
              ? `Reject Request #${currentRequest.internal_req_sl_no}` 
              : 'Reject Request'
          }
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason (Optional)"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={rejectionLoading}>
            Cancel
          </Button>
          <Button 
            onClick={confirmReject} 
            variant="contained" 
            color="error"
            disabled={rejectionLoading}
            startIcon={rejectionLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {rejectionLoading ? 'Processing...' : 'Confirm Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Modal */}
      <Dialog 
        open={imagePreviewOpen} 
        onClose={() => setImagePreviewOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0'
        }}>
          {previewTitle}
          <IconButton onClick={() => setImagePreviewOpen(false)} size="small">
            <CancelIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <Box
            component="img"
            src={previewImage}
            alt={previewTitle}
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: '80vh',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </DialogContent>
      </Dialog>

      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
};

export default RegistrationRequests;

